import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/util";
import {
  RefreshCw, Loader2, CheckCircle2, AlertTriangle, ChevronDown,
  Package, Plus, Pencil, Minus, Sparkles,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

/**
 * Admin action: run `nuclei -update-templates` on the server and render the
 * resulting diff (added / modified / removed) in a modal. Also surfaces the
 * current install state (binary present, templates on disk, version).
 */
export function NucleiTemplatesUpdater() {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [result, setResult] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [showLog, setShowLog] = useState(false);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const r = await api.get("/scans/nuclei/status");
      setStatus(r.data);
    } catch {
      setStatus(null);
    } finally { setStatusLoading(false); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const runUpdate = async () => {
    setUpdating(true);
    setResult(null);
    setModalOpen(true);
    try {
      const r = await api.post("/scans/nuclei/update-templates");
      setResult(r.data);
      if (r.data.already_up_to_date) {
        toast.success("Templates ya estaban al día");
      } else if (r.data.new_version) {
        toast.success(`Templates actualizados a ${r.data.new_version}`);
      } else if (r.data.ok) {
        toast.success("Actualización completada");
      } else {
        toast.error("Actualización terminó con errores — revisa el log");
      }
      loadStatus();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "No se pudo actualizar");
      setResult({ ok: false, error: ex?.response?.data?.detail || "error" });
    } finally { setUpdating(false); }
  };

  const totals = result?.totals || { total: 0, added: 0, modified: 0, removed: 0 };

  return (
    <section className="border border-border bg-card/60 p-4 space-y-3" data-testid="nuclei-templates-updater">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Package className="w-4 h-4 text-primary" />
          <div>
            <div className="overline">Nuclei · templates community</div>
            <div className="font-display text-sm">
              {statusLoading ? (
                <span className="text-muted-foreground font-mono text-xs">Cargando…</span>
              ) : status ? (
                <span className="font-mono text-xs" data-testid="nuclei-status-line">
                  <span className="text-foreground">{status.templates_on_disk?.toLocaleString?.() ?? 0}</span>
                  {" templates en disco · versión "}
                  <span className="text-primary">{status.templates_version || "desconocida"}</span>
                  {status.binary_available
                    ? <span className="text-sev-low"> · binario OK</span>
                    : <span className="text-sev-critical"> · binario ausente</span>}
                </span>
              ) : (
                <span className="text-sev-critical font-mono text-xs">Estado no disponible</span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={runUpdate}
          disabled={updating || !status?.binary_available}
          data-testid="nuclei-update-btn"
          className={cn(
            "px-4 py-2 flex items-center gap-2 text-xs font-mono uppercase tracking-widest border transition-colors",
            updating || !status?.binary_available
              ? "border-border text-muted-foreground cursor-not-allowed"
              : "border-primary text-primary hover:bg-primary hover:text-primary-foreground"
          )}
          title="Ejecuta `nuclei -update-templates` en el servidor (admin)"
        >
          {updating
            ? <Loader2 className="w-3 h-3 animate-spin" />
            : <RefreshCw className="w-3 h-3" />}
          {updating ? "Actualizando…" : "Actualizar templates"}
        </button>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl" data-testid="nuclei-update-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Package className="w-4 h-4 text-primary" />
              Actualización de templates de Nuclei
            </DialogTitle>
            <DialogDescription>
              Se ejecutó <code className="font-mono text-primary">nuclei -update-templates</code> en el servidor.
              Los cambios se aplican sobre <code className="font-mono">{status?.templates_dir}</code>.
            </DialogDescription>
          </DialogHeader>

          {updating && (
            <div className="flex items-center gap-2 py-8 text-muted-foreground text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Descargando el último release de projectdiscovery/nuclei-templates…
              puede tardar hasta 5 minutos.
            </div>
          )}

          {!updating && result && (
            <div className="space-y-4 mt-2">
              {/* Verdict banner */}
              <div className={cn(
                "border p-3 text-xs font-mono flex items-start gap-2",
                result.already_up_to_date
                  ? "border-sev-low/40 bg-sev-low/5 text-sev-low"
                  : result.ok
                  ? "border-primary/40 bg-primary/5 text-primary"
                  : "border-sev-critical/40 bg-sev-critical/5 text-sev-critical"
              )} data-testid="nuclei-update-verdict">
                {result.already_up_to_date
                  ? <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  : result.ok
                  ? <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  : <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                <div>
                  {result.already_up_to_date
                    ? "Sin cambios · los templates ya estaban en la última versión disponible."
                    : result.ok
                    ? <>
                        Actualización aplicada
                        {result.new_version && <> · nueva versión <b>{result.new_version}</b></>}
                        {result.previous_version && result.previous_version !== result.new_version
                          && <> (antes {result.previous_version})</>}
                        .
                      </>
                    : <>
                        La actualización terminó con errores (rc={result.rc}
                        {result.timed_out ? ", timeout" : ""}). Revisa el log.
                      </>}
                </div>
              </div>

              {/* Diff table */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <DiffTile icon={Package} label="Total" value={totals.total} />
                <DiffTile icon={Plus} label="Añadidos" value={totals.added} tone="primary" />
                <DiffTile icon={Pencil} label="Modificados" value={totals.modified} tone="medium" />
                <DiffTile icon={Minus} label="Removidos" value={totals.removed} tone="critical" />
              </div>

              {/* Before / after summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs font-mono">
                <div className="border border-border p-2">
                  <div className="overline">Antes</div>
                  <div className="text-foreground">
                    {result.before?.templates_on_disk?.toLocaleString?.() ?? 0} templates
                  </div>
                  <div className="text-muted-foreground">
                    versión {result.before?.templates_version || "—"}
                  </div>
                </div>
                <div className="border border-border p-2">
                  <div className="overline">Después</div>
                  <div className="text-foreground">
                    {result.after?.templates_on_disk?.toLocaleString?.() ?? 0} templates
                    {result.diff_disk?.templates_added_on_disk !== undefined
                      && result.diff_disk.templates_added_on_disk !== 0 && (
                      <span className={cn(
                        "ml-2 text-[10px]",
                        result.diff_disk.templates_added_on_disk > 0
                          ? "text-sev-low"
                          : "text-sev-medium"
                      )}>
                        ({result.diff_disk.templates_added_on_disk > 0 ? "+" : ""}
                        {result.diff_disk.templates_added_on_disk})
                      </span>
                    )}
                  </div>
                  <div className="text-muted-foreground">
                    versión {result.after?.templates_version || "—"}
                  </div>
                </div>
              </div>

              {/* Log toggle */}
              {(result.changelog_lines || []).length > 0 && (
                <div>
                  <button
                    onClick={() => setShowLog(v => !v)}
                    data-testid="nuclei-update-toggle-log"
                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
                  >
                    <ChevronDown className={cn("w-3 h-3 transition-transform", showLog && "rotate-180")} />
                    {showLog ? "Ocultar" : "Ver"} log completo ({result.changelog_lines.length} líneas)
                  </button>
                  {showLog && (
                    <pre className="mt-2 text-[10px] font-mono border border-border p-2 bg-background overflow-x-auto max-h-72"
                         data-testid="nuclei-update-log">
{result.changelog_lines.join("\n")}
                    </pre>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function DiffTile({ icon: Icon, label, value, tone }) {
  const cls = tone === "primary"   ? "text-primary"
            : tone === "medium"    ? "text-sev-medium"
            : tone === "critical"  ? "text-sev-critical"
            : "text-foreground";
  return (
    <div className="border border-border p-3 bg-background/60">
      <div className="flex items-center gap-1 overline text-[9px]">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={cn("font-display text-2xl leading-tight mt-1", cls)}>
        {typeof value === "number" ? value.toLocaleString() : value ?? 0}
      </div>
    </div>
  );
}
